import { createSlice } from '@reduxjs/toolkit';
import { HashOf, Nullable } from '@jamesgmarks/utilities';
import { IActionType } from '../../utils';

/**
 * Invoice counts broken down by status for a given month.
 * Populated by {@link loadInvoiceStatusCounts} and stored keyed by `monthsAgo` in `IDashboardState`.
 */
export interface IInvoiceStatusCounts {
  /** Total non-deleted, non-zero-dollar invoices for the period. */
  total: number;
  /** Invoices with `hydraState === 'unsent'` and no Freshbooks state (i.e. not yet pushed to accounting). */
  draft: number;
  /** Invoices with `hydraState === 'sent'` and `freshbooksState === 'draft'` (pushed but not finalised). */
  published: number;
  /** Invoices with `hydraState === 'sent'` and a non-draft, non-null Freshbooks state. */
  sent: number;
}

/** Redux state shape for the Dashboard feature slice. */
export interface IDashboardState {
  /** Loading state for dashboard data: `'idle'` | `'loading'` | `'loaded'`. */
  loadedState: string;
  /**
   * Map of `monthsAgo` → invoice status counts.
   * `0` = current month, `1` = last month, etc.
   * `null` means data has not been loaded for that offset yet.
   */
  invoiceStatusCountsByMonthsAgo: HashOf<Nullable<IInvoiceStatusCounts>>;
}

export const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState: {
    loadedState: 'idle',
    invoiceStatusCountsByMonthsAgo: {},
  } as IDashboardState,
  reducers: {
    setLoadingState: (state, action: IActionType<string>) => {
      state.loadedState = action.payload;
    },
    invoiceStatusCountsReceived: (state, action: IActionType<{
      monthsAgo: number,
      counts: IInvoiceStatusCounts,
    }>) => {
      state.invoiceStatusCountsByMonthsAgo[action.payload.monthsAgo] = action.payload.counts;
      state.loadedState = 'loaded';
    },
  },
});

export const {
  setLoadingState,
  invoiceStatusCountsReceived,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
